import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  booleanAttribute,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { merge, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { ItemSpecificationService } from 'src/app/admin/cf-admin/item-specification.service';
import { PaginationResponse } from 'src/app/Theme/shared/components/pagination/pagination.interface';
import { CustomSelectComponent } from 'src/app/Theme/shared/components/custom-select/custom-select.component';
import { SearchBarComponent } from 'src/app/Theme/shared/components/search-bar/search-bar.component';
import { CfCategoryService } from '../../cf-category/service/cf-category.service';
import { ProductService, PublishedItemsListParams } from '../service/product.service';
import {
  AppliedFilterChip,
  buildAppliedFilterChips,
  ProductSpecificationFilterSidebarService,
  ProductSpecificationFilterSelection,
  PRODUCT_SPEC_FILTER_PRICE_MAX,
  SpecFilterGroupChip,
  mergeSelectionIntoPublishedItemsListParams,
} from './product-specification-filter-sidebar.service';
import {
  FilterLayoutConfig,
  DEFAULT_FILTER_LAYOUT_CONFIG,
} from 'src/app/Theme/shared/components/filter/filter-layout.model';

/** Matches `_auth-responsive.scss` `$auth-phone-max` + tablet portrait mobile layout. */
const MOBILE_SHEET_MQ = '(max-width: 26.875rem), ((max-width: 767.98px) and (orientation: portrait))';

@Component({
  selector: 'app-product-specification-filter-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FeatherModule,
    SearchBarComponent,
    CustomSelectComponent,
  ],
  templateUrl: './product-specification-filter-sidebar.component.html',
  styleUrl: './product-specification-filter-sidebar.component.scss',
})
export class ProductSpecificationFilterSidebarComponent implements OnInit, OnDestroy {
  /**
   * When true, the panel is fixed to the viewport (below the main header), full window height.
   * When false, it flows in-page (legacy / narrow layouts).
   */
  @Input({ transform: booleanAttribute }) dockToWindow = true;

  /**
   * When true and not window-docked, panel sits inside the customer nav sidebar next to the category tree:
   * no outer card border so it reads as one unit with the tree rail.
   */
  @Input({ transform: booleanAttribute }) navLinked = false;

  /** Admin filter customizer: in-page preview without window docking or close control. */
  @Input({ transform: booleanAttribute }) previewMode = false;

  /** When set (e.g. admin preview), controls which filter blocks are shown. */
  @Input() displayConfig: FilterLayoutConfig | null = null;

  @HostBinding('class.spec-filter-host--preview')
  get previewHostClass(): boolean {
    return this.previewMode;
  }
  @HostBinding('class.spec-filter-host--window-dock')
  get windowDockHostClass(): boolean {
    return this.dockToWindow && !this.previewMode;
  }

  @HostBinding('class.spec-filter-host--mobile-sheet')
  get mobileSheetHostClass(): boolean {
    return this.isMobileSheet && this.dockToWindow && !this.previewMode;
  }

  get showPriceRangeSection(): boolean {
    const config = this.displayConfig ?? DEFAULT_FILTER_LAYOUT_CONFIG;
    return config.priceRangeEnabled !== false && config.priceRangeVisible !== false;
  }

  get showFilterSection(): boolean {
    const config = this.displayConfig ?? DEFAULT_FILTER_LAYOUT_CONFIG;
    return config.filterVisible !== false;
  }

  specGroups: any[] = [];
  loading = false;
  loadError = false;

  /** Dual-handle slider domain; set from `GET /CF/filtered-publish-list/max-price/` (no params). */
  absolutePriceMax = PRODUCT_SPEC_FILTER_PRICE_MAX;
  priceLow = 0;
  priceHigh = PRODUCT_SPEC_FILTER_PRICE_MAX;
  /** Which quick-select tile is active, if any (0–3). */
  activeQuickSlot: number | null = null;

  /** Four quartile slots for quick range (labels depend on {@link absolutePriceMax}). */
  readonly quickSlots: readonly number[] = [0, 1, 2, 3];

  selectedItemSpecs: string[] = [];

  /** Mobile bottom sheet: draft search + filter-by until “Show N results”. */
  mobileSearchDraft = '';
  mobileFilterMode: 'all' | 'order' = 'all';
  mobileSortValue: 'asc' | 'desc' | '' = '';
  readonly mobileSortOptions: readonly { label: string; value: 'asc' | 'desc' }[] = [
    { label: 'A - Z', value: 'asc' },
    { label: 'Z - A', value: 'desc' },
  ];
  isMobileSheet = false;

  /** Draft applied filters shown below the mobile search row. */
  appliedFilterChips: AppliedFilterChip[] = [];

  /** Live count for mobile footer CTA while the sheet is open (search + draft filters). */
  private mobilePreviewResultCount: number | null = null;

  @ViewChild(SearchBarComponent) private mobileSearchBar?: SearchBarComponent;

  private langSub?: Subscription;
  private openSub?: Subscription;
  private selectionSub?: Subscription;
  private mobilePreviewCountSub?: Subscription;
  private mobileMq?: MediaQueryList;
  private readonly mobilePreviewCountTrigger$ = new Subject<void>();
  private readonly mobileSearchPreviewTrigger$ = new Subject<void>();
  private readonly onMobileMqChange = (): void => {
    this.syncMobileSheetMode();
    this.cdr.markForCheck();
  };

  private syncMobileSheetMode(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      this.isMobileSheet = false;
      return;
    }
    this.mobileMq = this.mobileMq ?? window.matchMedia(MOBILE_SHEET_MQ);
    this.isMobileSheet = this.mobileMq.matches;
  }

  constructor(
    readonly specFilterSidebar: ProductSpecificationFilterSidebarService,
    private readonly productService: ProductService,
    private readonly categoryService: CfCategoryService,
    private readonly itemSpecificationService: ItemSpecificationService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mobileMq = window.matchMedia(MOBILE_SHEET_MQ);
      this.syncMobileSheetMode();
      this.mobileMq.addEventListener('change', this.onMobileMqChange);
    }

    if (!this.previewMode) {
      this.openSub = this.specFilterSidebar.isOpen$.subscribe((open) => {
        this.syncMobileSheetMode();
        if (open && this.isMobileSheet) {
          this.hydrateMobileDraft();
          this.mobilePreviewResultCount =
            this.specFilterSidebar.getCatalogBridgeSnapshot().resultCount;
          this.queueMobilePreviewCount();
          document.body.classList.add('spec-filter-mobile-open');
        } else {
          this.mobilePreviewResultCount = null;
          document.body.classList.remove('spec-filter-mobile-open');
        }
        this.cdr.markForCheck();
      });
      this.selectionSub = this.specFilterSidebar.filterSelection$.subscribe((sel) => {
        if (this.isMobileSheet && this.specFilterSidebar.isOpen) {
          return;
        }
        this.applySelectionFromService(sel);
      });
    }

    if (this.previewMode) {
      this.absolutePriceMax = 300;
      this.priceLow = 0;
      this.priceHigh = 300;
      this.loadPublishedSpecificationGroups();
      return;
    }

    const snap = this.specFilterSidebar.getFilterSelectionSnapshot();
    this.priceLow = snap.priceMin;
    this.priceHigh = snap.priceMax;
    this.selectedItemSpecs = [...snap.selectedItemSpecs];

    this.productService.getFilteredPublishListMaxPrice().subscribe({
      next: (data) => {
        const max = this.readMaxPrice(data);
        if (max == null) {
          return;
        }
        this.absolutePriceMax = max;
        this.specFilterSidebar.setPriceSliderMax(max);

        /** Only the out-of-box 0–500 snapshot; do not treat custom ranges as “initial”. */
        const isUnconfiguredDefault =
          snap.priceMin === 0 &&
          snap.priceMax === PRODUCT_SPEC_FILTER_PRICE_MAX &&
          snap.selectedItemSpecs.length === 0;

        if (isUnconfiguredDefault) {
          this.priceLow = 0;
          this.priceHigh = max;
          this.activeQuickSlot = null;
        } else {
          this.priceLow = Math.min(Math.max(0, this.priceLow), max);
          this.priceHigh = Math.min(Math.max(this.priceLow, this.priceHigh), max);
        }

        this.pushSelection();
        this.cdr.markForCheck();
      },
    });

    this.loadPublishedSpecificationGroups();

    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.refreshAppliedFilterChips();
      this.cdr.markForCheck();
    });

    if (!this.previewMode) {
      const previewCountRequest$ = () => {
        if (!this.isMobileSheet || !this.specFilterSidebar.isOpen) {
          return of(null);
        }
        const params = this.buildMobilePreviewListParams();
        const ctx = this.specFilterSidebar.getListContext();
        const request$ =
          ctx.mode === 'category' && ctx.categoryCode.trim()
            ? this.categoryService.getCategoryFilteredPublishList(ctx.categoryCode, {
                ...params,
                view_publish: true,
              })
            : this.productService.getPublishedItemsList(params);
        return request$.pipe(
          map(
            (response: PaginationResponse<unknown>) =>
              response?.pagination?.count ?? response?.results?.length ?? 0
          ),
          catchError(() => of(this.specFilterSidebar.getCatalogBridgeSnapshot().resultCount))
        );
      };

      this.mobilePreviewCountSub = merge(
        this.mobileSearchPreviewTrigger$.pipe(switchMap(() => previewCountRequest$())),
        this.mobilePreviewCountTrigger$.pipe(
          debounceTime(300),
          switchMap(() => previewCountRequest$())
        )
      ).subscribe((count) => {
        if (count != null) {
          this.mobilePreviewResultCount = count;
          this.cdr.markForCheck();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    this.openSub?.unsubscribe();
    this.selectionSub?.unsubscribe();
    this.mobilePreviewCountSub?.unsubscribe();
    this.mobilePreviewCountTrigger$.complete();
    this.mobileSearchPreviewTrigger$.complete();
    this.mobileMq?.removeEventListener('change', this.onMobileMqChange);
    document.body.classList.remove('spec-filter-mobile-open');
  }

  @HostListener('document:keydown.escape')
  onEscapeClose(): void {
    if (this.isMobileSheet && this.specFilterSidebar.isOpen && !this.previewMode) {
      this.close();
    }
  }

  get mobileResultCount(): number {
    if (this.isMobileSheet && this.specFilterSidebar.isOpen && this.mobilePreviewResultCount != null) {
      return this.mobilePreviewResultCount;
    }
    return this.specFilterSidebar.getCatalogBridgeSnapshot().resultCount;
  }

  get accordionDefaultOpen(): boolean {
    return !this.isMobileSheet;
  }

  close(): void {
    if (this.previewMode) {
      return;
    }
    this.specFilterSidebar.close();
  }

  resetFilters(): void {
    if (this.isMobileSheet) {
      const cap = this.specFilterSidebar.getPriceSliderCeiling();
      this.priceLow = 0;
      this.priceHigh = cap;
      this.selectedItemSpecs = [];
      this.activeQuickSlot = null;
      this.mobileSearchDraft = '';
      this.mobileFilterMode = 'all';
      this.mobileSortValue = '';
      this.refreshAppliedFilterChips();
      this.queueMobilePreviewCount();
      this.cdr.markForCheck();
      return;
    }

    this.specFilterSidebar.resetFilterSelection();
    const snap = this.specFilterSidebar.getFilterSelectionSnapshot();
    this.priceLow = snap.priceMin;
    this.priceHigh = snap.priceMax;
    this.selectedItemSpecs = [...snap.selectedItemSpecs];
    this.activeQuickSlot = null;
    this.cdr.markForCheck();
  }

  onMobileSearchDraft(term: string): void {
    this.mobileSearchDraft = term ?? '';
    this.queueMobileSearchPreviewCount();
    this.refreshAppliedFilterChips();
  }

  submitMobileSearch(): void {
    const term = this.mobileSearchBar?.searchControl.value ?? this.mobileSearchDraft ?? '';
    this.onMobileSearchDraft(term);
  }

  onMobileFilterModeChange(mode: 'all' | 'order'): void {
    this.mobileFilterMode = mode;
    if (mode === 'all') {
      this.mobileSortValue = '';
    }
    this.queueMobilePreviewCount();
    this.refreshAppliedFilterChips();
    this.cdr.markForCheck();
  }

  onMobileSortChange(order: 'asc' | 'desc' | string): void {
    if (order === 'asc' || order === 'desc') {
      this.mobileFilterMode = 'order';
      this.mobileSortValue = order;
      this.queueMobilePreviewCount();
      this.refreshAppliedFilterChips();
      this.cdr.markForCheck();
    }
  }

  trackAppliedFilterChip(_: number, chip: AppliedFilterChip): string {
    return chip.id;
  }

  removeAppliedFilterChip(chip: AppliedFilterChip, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (chip.id === 'search') {
      this.mobileSearchDraft = '';
      this.mobileSearchBar?.searchControl.setValue('', { emitEvent: false });
    } else if (chip.id === 'filterBy') {
      this.mobileFilterMode = 'all';
      this.mobileSortValue = '';
    } else if (chip.id === 'priceRange') {
      const cap = this.specFilterSidebar.getPriceSliderCeiling();
      this.priceLow = 0;
      this.priceHigh = Math.max(cap, this.absolutePriceMax);
      this.activeQuickSlot = null;
    } else if (chip.groupId) {
      const tokens =
        chip.tokens?.length
          ? chip.tokens
          : this.specFilterSidebar.getSpecGroupTokens(chip.groupId);
      const drop = new Set(tokens.map((t) => String(t).trim()).filter(Boolean));
      this.selectedItemSpecs = this.selectedItemSpecs.filter((t) => !drop.has(t));
    }

    this.refreshAppliedFilterChips();
    this.queueMobilePreviewCount();
    this.cdr.markForCheck();
  }

  get mobileTypeSelectEnabled(): boolean {
    return this.mobileFilterMode === 'order';
  }

  applyMobileFilters(): void {
    if (!this.isMobileSheet) {
      return;
    }
    const selection: ProductSpecificationFilterSelection = {
      priceMin: this.priceLow,
      priceMax: this.priceHigh,
      selectedItemSpecs: [...this.selectedItemSpecs],
    };
    this.syncSpecGroupChipsAndTokens();
    this.specFilterSidebar.applyMobileFilters(
      {
        searchTerm: (this.mobileSearchDraft || '').trim(),
        filterMode: this.mobileFilterMode,
        sortOrder: this.mobileFilterMode === 'order' ? this.mobileSortValue : '',
      },
      selection
    );
  }

  private hydrateMobileDraft(): void {
    const snap = this.specFilterSidebar.getFilterSelectionSnapshot();
    const bridge = this.specFilterSidebar.getCatalogBridgeSnapshot();
    this.priceLow = snap.priceMin;
    this.priceHigh = snap.priceMax;
    this.selectedItemSpecs = [...snap.selectedItemSpecs];
    this.mobileSearchDraft = bridge.searchTerm ?? '';
    this.mobileFilterMode = bridge.filterMode ?? 'all';
    this.mobileSortValue = bridge.sortOrder ?? '';
    this.refreshAppliedFilterChips();
    this.queueMobilePreviewCount();
    this.cdr.markForCheck();
  }

  private queueMobilePreviewCount(): void {
    if (!this.isMobileSheet || !this.specFilterSidebar.isOpen || this.previewMode) {
      return;
    }
    this.mobilePreviewCountTrigger$.next();
  }

  /** Search bar already debounces input; preview count updates immediately after each emit. */
  private queueMobileSearchPreviewCount(): void {
    if (!this.isMobileSheet || !this.specFilterSidebar.isOpen || this.previewMode) {
      return;
    }
    this.mobileSearchPreviewTrigger$.next();
  }

  private buildMobilePreviewListParams(): PublishedItemsListParams {
    const sortOrder =
      this.mobileFilterMode === 'order' &&
      (this.mobileSortValue === 'asc' || this.mobileSortValue === 'desc')
        ? this.mobileSortValue
        : 'asc';
    const base: PublishedItemsListParams = {
      page: 1,
      page_size: 1,
      sort_by: 'item',
      sort_order: sortOrder,
    };
    const term = (this.mobileSearchDraft || '').trim();
    if (term) {
      base.item = term;
    }
    const draftSelection: ProductSpecificationFilterSelection = {
      priceMin: this.priceLow,
      priceMax: this.priceHigh,
      selectedItemSpecs: [...this.selectedItemSpecs],
    };
    return mergeSelectionIntoPublishedItemsListParams(
      draftSelection,
      base,
      this.absolutePriceMax
    );
  }

  trackByGroupId(_: number, g: any): string {
    return g?.id_set ?? String(_);
  }

  /**
   * Must be an arrow so Angular's iterable differ does not call it with a wrong `this`
   * (plain methods lose component context when used as `trackBy`).
   */
  readonly trackByItemSpec = (_: number, s: any): string =>
    this.specFilterRowKey(s) || String(_);

  /** CF `ItemSpecification` template id (camelCase or snake_case from API). */
  specId(spec: any): string {
    const id = spec?.itemSpec ?? spec?.item_spec;
    return id != null && String(id).trim() !== '' ? String(id).trim() : '';
  }

  /** `CF_SpecificationByItem` primary key for item-only lines (no template `itemSpec`). */
  specByItemPk(spec: any): string {
    const pk = spec?.specByItem_pk ?? spec?.specByItemPk;
    return pk != null && String(pk).trim() !== '' ? String(pk).trim() : '';
  }

  /** Stable English key when the spec is item-only and not linked to a template row. */
  nameEnKey(spec: any): string {
    const k = spec?.name_en_key ?? spec?.nameEnKey;
    return k != null && String(k).trim() !== '' ? String(k).trim() : '';
  }

  /** Row identity for `trackBy` (template id, item-only pk, or `name_en_key`). */
  specFilterRowKey(spec: any): string {
    return this.specId(spec) || this.specByItemPk(spec) || this.nameEnKey(spec);
  }

  /** Whether this spec row should appear in the filter (has filterable line values). */
  hasFilterableSpec(spec: any): boolean {
    return this.distinctLineValues(spec).length > 0;
  }

  sortedSpecifications(group: any): any[] {
    const list = Array.isArray(group?.specifications) ? group.specifications : [];
    return [...list].sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0));
  }

  /**
   * Visible title for each group: multilingual `specificationSet` (API field), then `id_set` if the name is empty.
   */
  specificationSetName(group: any): string {
    const name = this.pickMl(group?.specificationSet);
    if (name) {
      return name;
    }
    return group?.id_set != null ? String(group.id_set) : '';
  }

  /** Localized label for one row: `name` on the specification (Length, Width, …). */
  specificationDisplayName(spec: any): string {
    return (
      this.pickMl(spec?.name) ||
      this.nameEnKey(spec) ||
      this.specId(spec) ||
      this.specByItemPk(spec)
    );
  }

  /** Multilingual `distinct_line_values[]` entries from the published specifications API. */
  distinctLineValues(spec: any): unknown[] {
    const raw = spec?.distinct_line_values ?? spec?.distinctLineValues;
    return Array.isArray(raw) ? raw : [];
  }

  /** Localized label for one distinct line value object. */
  lineValueLabel(line: unknown): string {
    return this.pickMl(line);
  }

  /** Product count for a filter value when the API provides it; otherwise null (badge hidden). */
  lineValueCount(line: unknown): number | null {
    if (line == null || typeof line !== 'object' || Array.isArray(line)) {
      return null;
    }
    const rec = line as Record<string, unknown>;
    const raw =
      rec['count'] ??
      rec['product_count'] ??
      rec['productCount'] ??
      rec['item_count'] ??
      rec['itemCount'];
    if (raw == null) {
      return null;
    }
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  /** Hide nested spec titles when a group only has one filterable specification row. */
  showSpecBlockName(group: any): boolean {
    return this.sortedSpecifications(group).filter((s) => this.hasFilterableSpec(s)).length > 1;
  }

  isSpecValueSelected(spec: any, line: unknown): boolean {
    const token = this.specValueToken(spec, line);
    return token ? this.selectedItemSpecs.includes(token) : false;
  }

  toggleSpecValue(spec: any, line: unknown, checked: boolean): void {
    const token = this.specValueToken(spec, line);
    if (!token) {
      return;
    }
    if (checked) {
      if (!this.selectedItemSpecs.includes(token)) {
        this.selectedItemSpecs = [...this.selectedItemSpecs, token];
      }
    } else {
      this.selectedItemSpecs = this.selectedItemSpecs.filter((t) => t !== token);
    }
    this.pushSelection();
  }

  /**
   * Stable token for selection + `item_specs`:
   * - Template spec: `itemSpec|<url-encoded canonical value>`
   * - Item-only spec: `specByItem_pk|<url-encoded canonical value>` (pk on line or spec)
   * - Fallback when pk is absent: `name_en_key|<url-encoded canonical value>`
   * Canonical text prefers `en` on multilingual line objects so the query stays stable across UI language.
   */
  specValueToken(spec: any, line: unknown): string {
    const canon = this.canonicalLineValueForFilter(line);
    if (!canon) {
      return '';
    }
    const encoded = encodeURIComponent(canon);
    const itemSpec = this.specId(spec);
    if (itemSpec) {
      return `${itemSpec}|${encoded}`;
    }
    const linePk = this.lineSpecByItemPk(line);
    const specPk = this.specByItemPk(spec);
    const pk = linePk || specPk;
    if (pk) {
      return `${pk}|${encoded}`;
    }
    const key = this.nameEnKey(spec);
    if (key) {
      return `${key}|${encoded}`;
    }
    return '';
  }

  /** `specByItem_pk` on a single `distinct_line_values` entry when provided per value. */
  private lineSpecByItemPk(line: unknown): string {
    if (line == null || typeof line !== 'object' || Array.isArray(line)) {
      return '';
    }
    const pk = (line as Record<string, unknown>)['specByItem_pk'] ?? (line as Record<string, unknown>)['specByItemPk'];
    return pk != null && String(pk).trim() !== '' ? String(pk).trim() : '';
  }

  readonly trackByLineIndex = (index: number, _line: unknown): number => index;

  onPriceLowInput(v: number): void {
    const next = Math.max(0, Math.min(this.absolutePriceMax, Math.round(Number(v))));
    this.priceLow = Math.min(next, this.priceHigh);
    this.activeQuickSlot = null;
    this.pushSelection();
  }

  onPriceHighInput(v: number): void {
    const next = Math.max(0, Math.min(this.absolutePriceMax, Math.round(Number(v))));
    this.priceHigh = Math.max(next, this.priceLow);
    this.activeQuickSlot = null;
    this.pushSelection();
  }

  applyQuickRange(lo: number, hi: number, slot: number): void {
    const cap = this.absolutePriceMax;
    const loR = Math.max(0, Math.min(Math.round(lo), cap));
    const hiR = Math.max(0, Math.min(Math.round(hi), cap));
    this.priceLow = Math.min(loR, hiR);
    this.priceHigh = Math.max(loR, hiR);
    this.activeQuickSlot = slot;
    this.pushSelection();
  }

  /** Splits [0, max] into four contiguous ranges (quartiles). */
  quartileRange(slot: number): { lo: number; hi: number } {
    const m = Math.max(1, Math.floor(this.absolutePriceMax));
    const at = (i: number) => Math.round((m * i) / 4);
    const edges = [0, at(1), at(2), at(3), m];
    const lo = edges[slot] ?? 0;
    const hi = edges[(slot + 1) as number] ?? m;
    return { lo, hi: Math.max(lo, hi) };
  }

  quickSlotLabel(slot: number): string {
    const { lo, hi } = this.quartileRange(slot);
    return `${this.formatPriceLabel(lo)} – ${this.formatPriceLabel(hi)}`;
  }

  applyQuickSlot(slot: number): void {
    const { lo, hi } = this.quartileRange(slot);
    this.applyQuickRange(lo, hi, slot);
  }

  priceTrackGradient(): string {
    const max = this.absolutePriceMax || 1;
    const a = (this.priceLow / max) * 100;
    const b = (this.priceHigh / max) * 100;
    const fill = 'var(--cf-spec-filter-range-fill, var(--theme-primary))';
    const track = 'var(--theme-gray-200)';
    return `linear-gradient(to right, ${track} 0%, ${track} ${a}%, ${fill} ${a}%, ${fill} ${b}%, ${track} ${b}%, ${track} 100%)`;
  }

  formatPriceLabel(n: number): string {
    return `$${Math.round(n)}`;
  }

  /** Syncs sidebar controls when a chip is removed from the product list. */
  private applySelectionFromService(sel: ProductSpecificationFilterSelection): void {
    this.priceLow = sel.priceMin;
    this.priceHigh = sel.priceMax;
    this.selectedItemSpecs = [...sel.selectedItemSpecs];
    const cap = this.specFilterSidebar.getPriceSliderCeiling();
    if (sel.priceMin <= 0 && sel.priceMax >= cap) {
      this.activeQuickSlot = null;
    }
    if (this.specGroups.length) {
      this.syncSpecGroupChipsAndTokens();
    }
    this.refreshAppliedFilterChips();
    this.cdr.markForCheck();
  }

  private pushSelection(): void {
    if (this.isMobileSheet && this.specFilterSidebar.isOpen) {
      this.queueMobilePreviewCount();
      this.refreshAppliedFilterChips();
      return;
    }
    this.commitSelectionToService();
  }

  private appliedFilterChipLabels() {
    return {
      search: (term: string) => this.translate.instant('CFproduct.searchFilterChip', { term }),
      priceRange: this.translate.instant('CFproduct.priceRange'),
      filterBy: (sort: string) => this.translate.instant('CFproduct.filterByChip', { sort }),
      sortAsc: this.translate.instant('CFproduct.sortAsc'),
      sortDesc: this.translate.instant('CFproduct.sortDesc'),
    };
  }

  private refreshAppliedFilterChips(): void {
    if (!this.isMobileSheet || this.previewMode) {
      this.appliedFilterChips = [];
      return;
    }
    const cap = this.specFilterSidebar.getPriceSliderCeiling();
    this.appliedFilterChips = buildAppliedFilterChips({
      searchTerm: this.mobileSearchDraft,
      filterMode: this.mobileFilterMode,
      sortOrder: this.mobileFilterMode === 'order' ? this.mobileSortValue : '',
      specGroupChips: this.buildSpecGroupChipsFromSelection(this.selectedItemSpecs),
      labels: this.appliedFilterChipLabels(),
      priceMin: this.priceLow,
      priceMax: this.priceHigh,
      priceCeiling: Math.max(cap, this.absolutePriceMax),
    });
  }

  private buildSpecGroupChipsFromSelection(selectedItemSpecs: string[]): SpecFilterGroupChip[] {
    const chips: SpecFilterGroupChip[] = [];
    const tokenIndex: Record<string, string[]> = {};

    for (const group of this.specGroups) {
      const groupId = group?.id_set != null ? String(group.id_set) : '';
      if (!groupId) {
        continue;
      }
      const tokens: string[] = [];
      for (const spec of this.sortedSpecifications(group)) {
        if (!this.hasFilterableSpec(spec)) {
          continue;
        }
        for (const line of this.distinctLineValues(spec)) {
          const token = this.specValueToken(spec, line);
          if (token && selectedItemSpecs.includes(token)) {
            tokens.push(token);
          }
        }
      }
      if (tokens.length) {
        tokenIndex[groupId] = tokens;
        chips.push({ groupId, label: this.specificationSetName(group), tokens });
      }
    }

    return chips;
  }

  private commitSelectionToService(): void {
    const next: ProductSpecificationFilterSelection = {
      priceMin: this.priceLow,
      priceMax: this.priceHigh,
      selectedItemSpecs: [...this.selectedItemSpecs],
    };
    this.specFilterSidebar.setFilterSelection(next);
    this.syncSpecGroupChipsAndTokens();
  }

  private syncSpecGroupChipsAndTokens(): void {
    const chips = this.buildSpecGroupChipsFromSelection(this.selectedItemSpecs);
    const tokenIndex: Record<string, string[]> = {};
    for (const chip of chips) {
      tokenIndex[chip.groupId] = [...chip.tokens];
    }
    this.specFilterSidebar.setActiveSpecGroupChips(chips);
    this.specFilterSidebar.setSpecGroupTokenIndex(tokenIndex);
  }

  /**
   * API shape e.g. `{ "max_price": "22000.00" }` (string or number is fine).
   */
  private readMaxPrice(data: unknown): number | null {
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
      const o = data as Record<string, unknown>;
      const v = o['max_price'] ?? o['maxPrice'];
      if (v === undefined || v === null) {
        return null;
      }
      const n =
        typeof v === 'number'
          ? v
          : parseFloat(String(v).replace(/,/g, '').trim());
      if (!Number.isFinite(n) || n <= 0) {
        return null;
      }
      return Math.max(1, Math.ceil(n));
    }
    if (typeof data === 'number' && Number.isFinite(data) && data > 0) {
      return Math.max(1, Math.ceil(data));
    }
    return null;
  }

  private loadPublishedSpecificationGroups(): void {
    this.loading = true;
    this.loadError = false;
    const collected: any[] = [];
    const pageSize = 50;

    const fetchPage = (page: number): void => {
      this.itemSpecificationService
        .getPublishedSpecificationSetsWithSpecifications({ page, page_size: pageSize })
        .subscribe({
          next: (res: PaginationResponse<any>) => {
            const rows = res?.results ?? [];
            collected.push(...rows);
            const p = res?.pagination;
            if (p?.has_next === true && p?.next_page != null) {
              fetchPage(p.next_page);
            } else {
              const hasDistinctValues = (s: any): boolean => {
                const raw = s?.distinct_line_values ?? s?.distinctLineValues;
                return Array.isArray(raw) && raw.length > 0;
              };
              this.specGroups = collected
                .map((g) => ({
                  ...g,
                  specifications: (Array.isArray(g?.specifications) ? g.specifications : []).filter(
                    hasDistinctValues
                  ),
                }))
                .filter((g) => g.specifications.length > 0);
              this.loading = false;
              this.cdr.markForCheck();
              if (this.isMobileSheet && this.specFilterSidebar.isOpen) {
                this.refreshAppliedFilterChips();
              } else {
                this.syncSpecGroupChipsAndTokens();
              }
            }
          },
          error: () => {
            this.loadError = true;
            this.specGroups = [];
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
    };

    fetchPage(1);
  }

  /**
   * Resolves CF multilingual fields as returned by
   * `GET /CF/specification-sets/published/specifications/`
   * (`specificationSet`, `description`, `name`: `{ "en": "...", "fr": "..." }`, etc.).
   * Empty strings for one locale are skipped; we try current language, then en, fr, nl, then any value.
   */
  private pickMl(val: unknown): string {
    if (val == null) {
      return '';
    }
    if (typeof val === 'string') {
      return val.trim();
    }
    if (typeof val !== 'object' || Array.isArray(val)) {
      return String(val).trim();
    }

    const rec = val as Record<string, string | undefined>;
    const t = (s: unknown): string => (typeof s === 'string' ? s.trim() : '');

    const lang = (this.translate.currentLang || 'en').toLowerCase();
    const tryKeys = [lang, 'en', 'fr', 'nl', 'de'];
    const seen = new Set<string>();
    for (const k of tryKeys) {
      if (seen.has(k)) {
        continue;
      }
      seen.add(k);
      const out = t(rec[k]);
      if (out) {
        return out;
      }
    }

    for (const v of Object.values(rec)) {
      const out = t(v);
      if (out) {
        return out;
      }
    }

    return '';
  }

  /**
   * Single locale-independent string for one line value (for filter tokens and backend matching).
   */
  private canonicalLineValueForFilter(line: unknown): string {
    if (line == null) {
      return '';
    }
    if (typeof line === 'string') {
      return line.trim();
    }
    if (typeof line === 'object' && !Array.isArray(line)) {
      const rec = line as Record<string, unknown>;
      const t = (k: string): string => {
        const v = rec[k];
        return typeof v === 'string' ? v.trim() : '';
      };
      const en = t('en');
      if (en) {
        return en;
      }
      const fr = t('fr');
      if (fr) {
        return fr;
      }
      for (const v of Object.values(rec)) {
        if (typeof v === 'string' && v.trim()) {
          return v.trim();
        }
      }
    }
    return this.pickMl(line);
  }
}
